import * as React from 'react';

import { queryParamsStore } from '@/stores/global/RootStore';
import type { OrganizationChatAiPageStore } from '@/stores/local/pages/OrganizationChatAiPageStore';

type UseChatPanelStoreSessionSyncParams = {
  opened: boolean;
  organizationId: number;
  documentId?: string;
  sessionIdFromUrl: string | null;
  seedText?: string;
  analysisResultId?: number;
  store: OrganizationChatAiPageStore;
};

/**
 * Синхронизация OrganizationChatAiPageStore для drawer чата над документами:
 * сброс при смене орг/документа, восстановление сессии из query, seed из анализа, контекст и init.
 * Паттерн близок к OrganizationPageAiChat + useChatNavigationState (эффекты разделены по смыслу).
 */
export const useChatPanelStoreSessionSync = ({
  opened,
  organizationId,
  documentId,
  sessionIdFromUrl,
  seedText,
  analysisResultId,
  store,
}: UseChatPanelStoreSessionSyncParams): void => {
  const lastScopeKeyRef = React.useRef<string | null>(null);
  const lastRestoredSessionIdRef = React.useRef<string | null>(null);
  const appliedSeedKeyRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    const scopeKey = `${organizationId}:${documentId ?? ''}`;
    const prevScope = lastScopeKeyRef.current;
    const scopeChanged = prevScope !== null && prevScope !== scopeKey;

    if (scopeChanged) {
      store.resetToEmptyState();
      queryParamsStore.setParam('session_id', undefined);
      lastRestoredSessionIdRef.current = null;
      appliedSeedKeyRef.current = null;
    }

    lastScopeKeyRef.current = scopeKey;
  }, [organizationId, documentId, store]);

  React.useEffect(() => {
    if (!opened) {
      return;
    }

    const scopeKey = `${organizationId}:${documentId ?? ''}`;

    const sync = async () => {
      if (sessionIdFromUrl) {
        if (lastRestoredSessionIdRef.current !== sessionIdFromUrl) {
          lastRestoredSessionIdRef.current = sessionIdFromUrl;
          await store.restoreSessionFromUrl(sessionIdFromUrl);
        }
      } else {
        const seedKey = `${scopeKey}\0${seedText ?? ''}\0${String(analysisResultId ?? '')}`;

        if (appliedSeedKeyRef.current !== seedKey) {
          store.setPendingSeedText(seedText);
          appliedSeedKeyRef.current = seedKey;
        }
      }

      store.setFixedContextDocuments(documentId ? [documentId] : []);
      store.setSelectionLockedToAll(!documentId);
      store.setAnalysisContext(analysisResultId);
      await store.init();
    };

    void sync();
  }, [opened, organizationId, documentId, sessionIdFromUrl, seedText, analysisResultId, store]);
};
